import React, { useEffect, useState } from 'react';
import Logo from '../../components/common/Logo';
import AdminSidebar from '../../components/admin/AdminSidebar';
import UserService from '../../services/user.service';
import { useParams, useNavigate } from 'react-router-dom';
import AdminPatientSurveyResponses from '../../components/admin/AdminPatientSurveryResponse';
import AdminPatientAppointments from '../../components/admin/AdminPatientAppointments';
import { User, ClipboardList, Calendar, Trash2, ArrowLeft, MapPin, FileText, Users } from 'lucide-react';
import DeleteUserButton from '../../components/DeleteUserButton';
import { formatDate } from '../../utils/dateUtils';
import AdminPatientTherapists from "../../components/admin/AdminPatientTherapists"

const AdminDetailsUserPage = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('anagrafica'); // 'anagrafica', 'questionario', 'appuntamenti', 'cancellazione'
  const { userType, userId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    loadUserDetails(userId);
  }, [userId]);

  const loadUserDetails = async (id) => {
    try {
      const data = await UserService.getUserDetails(id);
      setUser(data);
    } catch (error) {
      console.error('Errore durante il caricamento dei dettagli dell\'utente:', error);
    }
  };

  const handleToggleLogin = async (userId) => {
    try {
      await UserService.toggleUserLogin(userId);
      loadUserDetails(userId);
    } catch (error) {
      console.error('[DEBUG] Error toggling login status:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Helper per formattare i valori null
  const formatValue = (value) => {
    return value || 'Non settato';
  };

  // Helper per formattare boolean
  const formatBoolean = (value) => {
    if (value === null || value === undefined) return 'Non settato';
    return value ? 'Sì' : 'No';
  };

  // Helper per formattare l'indirizzo completo
  const getFullAddress = () => {
    if (!user) return 'Non settato';

    const parts = [];
    if (user.street) {
      parts.push(user.street + (user.civic_number ? ' ' + user.civic_number : ''));
    }
    if (user.postal_code && user.city) {
      parts.push(user.postal_code + ' ' + user.city);
    }
    if (user.province) {
      parts.push('(' + user.province + ')');
    }
    return parts.length > 0 ? parts.join(', ') : 'Non settato';
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <AdminSidebar />
      </div>

      {/* Main content */}
      <div className="flex-1 bg-gray-50 p-8">
        <header className="mb-8">
          <button
            onClick={() => navigate(`/admin/${userType}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla lista
          </button>

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold mb-2">
                {userType === 'doctors' ? 'Dettagli Professionista' : 'Dettagli Paziente'}
              </h1>
              {user && <p className="text-gray-600">{user.email}</p>}
            </div>

            {userType === 'doctors' && user && (
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <span className={`inline-block w-3 h-3 rounded-full mr-2 ${user.login_enabled ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                  <span className="text-sm text-gray-600">
                    Login {user.login_enabled ? 'Abilitato' : 'Disabilitato'}
                  </span>
                </div>
                <button
                  onClick={() => handleToggleLogin(user.id)}
                  className={`px-4 py-2 rounded-lg flex items-center ${user.login_enabled
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-green-500 hover:bg-green-600'
                    } text-white transition-colors`}
                >
                  {user.login_enabled ? 'Disabilita' : 'Abilita'}
                </button>
              </div>
            )}
          </div>
        </header>

        {user ? (
          <>
            {/* Tabs Navigation */}
            <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
              <button
                className={`py-3 px-6 font-medium flex items-center border-b-2 ${activeTab === 'anagrafica'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => handleTabChange('anagrafica')}
              >
                <User className="w-5 h-5 mr-2" />
                Anagrafica
              </button>
              <button
                className={`py-3 px-6 font-medium flex items-center border-b-2 ${activeTab === 'questionario'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => handleTabChange('questionario')}
              >
                <ClipboardList className="w-5 h-5 mr-2" />
                Questionario
              </button>
              <button
                className={`py-3 px-6 font-medium flex items-center border-b-2 ${activeTab === 'appuntamenti'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => handleTabChange('appuntamenti')}
              >
                <Calendar className="w-5 h-5 mr-2" />
                Appuntamenti
              </button>
              <button
                className={`py-3 px-6 font-medium flex items-center border-b-2 ${activeTab === 'professionista'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => handleTabChange('professionista')}
              >
                <Users className="w-5 h-5 mr-2" />
                Professionista
              </button>
              <button
                className={`py-3 px-6 font-medium flex items-center border-b-2 ${activeTab === 'cancellazione'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => handleTabChange('cancellazione')}
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Cancellazione
              </button>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === 'anagrafica' && (
                <div className="space-y-6">
                  {/* Dati Personali */}
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <User className="w-6 h-6 mr-2 text-purple-600" />
                      Dati Personali
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>Nome:</strong> {formatValue(user.first_name)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Cognome:</strong> {formatValue(user.last_name)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Email:</strong> {formatValue(user.email)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>Telefono:</strong> {formatValue(user.phone)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Genere:</strong> {formatValue(user.gender)}
                        </p>
                        {userType === 'patients' && (
                          <p className="text-gray-600 mb-2">
                            <strong>Data di nascita:</strong> {formatValue(user.birth_date)}<br />
                            <strong>Luogo di nascita:</strong> {formatValue(user.birth_place)}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>Codice Fiscale:</strong> {formatValue(user.fiscal_code)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>P.IVA:</strong> {formatValue(user.pi)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Note:</strong> {formatValue(user.notes)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Indirizzo */}
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <MapPin className="w-6 h-6 mr-2 text-blue-600" />
                      Indirizzo
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>Via/Strada:</strong> {formatValue(user.street)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Numero Civico:</strong> {formatValue(user.civic_number)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>CAP:</strong> {formatValue(user.postal_code)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Città:</strong> {formatValue(user.city)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>Provincia:</strong> {formatValue(user.province)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Indirizzo Completo:</strong> {getFullAddress()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dati Specifici per Tipo Utente */}
                  {userType === 'doctors' && user.therapist && (
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h2 className="text-xl font-semibold mb-4">Informazioni Professionali</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-600 mb-2">
                            <strong>Specializzazione:</strong> {formatValue(user.therapist.specialization)}
                          </p>
                          <p className="text-gray-600 mb-2">
                            <strong>Indirizzo Studio:</strong> {formatValue(user.therapist.office_address)}
                          </p>
                          <p className="text-gray-600 mb-2">
                            <strong>Status:</strong> {formatValue(user.therapist.status)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600 mb-2">
                            <strong>Prezzo Orario:</strong> {formatValue(user.therapist.default_price_hour)} €
                          </p>
                          <p className="text-gray-600 mb-2">
                            <strong>Commissione:</strong> {formatValue(user.therapist.commission_rate)} %
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fatturazione (solo per pazienti) */}
                  {userType === 'patients' && user.patient && (
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h2 className="text-xl font-semibold mb-4 flex items-center">
                        <FileText className="w-6 h-6 mr-2 text-green-600" />
                        Fatturazione
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-600 mb-2">
                            <strong>Invio Fattura Sistema Sanitario:</strong> {formatBoolean(user.patient.invio_fattura_sistema_sanitaria)}
                          </p>
                          <p className="text-gray-600 mb-2">
                            <strong>Note per la fatturazione:</strong> {formatValue(user.patient.medical_history)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Informazioni Sistema */}
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Informazioni Sistema</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>ID Utente:</strong> {user.id}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Ruolo:</strong> {formatValue(user.role)}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Login Abilitato:</strong> {formatBoolean(user.login_enabled)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-2">
                          <strong>Data Registrazione:</strong> {user.created_at ? formatDate(user.created_at) : 'Non settato'}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Ultimo Aggiornamento:</strong> {user.updated_at ? formatDate(user.updated_at) : 'Non settato'}
                        </p>
                        <p className="text-gray-600 mb-2">
                          <strong>Codice Provenienza:</strong> {formatValue(user.provenance_code)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'questionario' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-lg font-semibold mb-4">Risposte al questionario</h2>
                  <AdminPatientSurveyResponses userId={userId} />
                </div>
              )}

              {activeTab === 'appuntamenti' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-lg font-semibold mb-4">Gestione Appuntamenti</h2>
                  <AdminPatientAppointments patientId={userId} />
                </div>
              )}

              {activeTab === 'cancellazione' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-lg font-semibold mb-4">Cancellazione</h2>
                  <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 mb-2">
                      <strong>Attenzione:</strong> La cancellazione di un utente è un&apos; operazione irreversibile.
                    </p>
                    <p className="text-red-600">
                      Tutti i dati associati a questo utente verranno rimossi dal sistema.
                    </p>
                  </div>
                  <DeleteUserButton
                    userId={user.id}
                    userName={`${user.first_name} ${user.last_name}`}
                    onSuccess={() => {
                      navigate(`/admin/${userType}`);
                    }}
                  />
                </div>
              )}

              {activeTab === 'professionista' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-lg font-semibold mb-4">Associa un professionista al paziente</h2>
                  <AdminPatientTherapists userId={userId} />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500">Caricamento dettagli utente...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDetailsUserPage;
